import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import './CustomerService.css';

export default function CustomerService() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'terms');

  useEffect(() => {
    const tab = searchParams.get('tab') || 'terms';
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [searchParams]);

  const handleTabChange = (tab) => {
    setSearchParams({ tab });
  };

  const tabs = [
    { id: 'terms', label: 'Terms & Conditions' },
    { id: 'privacy', label: 'Privacy Policy' },
    { id: 'shipping', label: 'Shipping Info' },
    { id: 'returns', label: 'Returns & Exchanges' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <div className="cs-page">
      <div className="cs-container">

        {/* Header */}
        <div className="cs-header">
          <h1 className="cs-main-title">Customer Service</h1>
          <p className="cs-main-subtitle">
            Everything you need to know about shopping with VKTrend
          </p>
        </div>

        {/* Tabs */}
        <div className="cs-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`cs-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="cs-content">
          {activeTab === 'terms' && <TermsSection />}
          {activeTab === 'privacy' && <PrivacySection />}
          {activeTab === 'shipping' && <ShippingSection />}
          {activeTab === 'returns' && <ReturnsSection />}
          {activeTab === 'faq' && <FAQSection />}
        </div>

        {/* Back to Home */}
        <div className="cs-footer">
          <Link to="/" className="cs-back-btn">← Back to Home</Link>
        </div>

      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   1. TERMS & CONDITIONS
═══════════════════════════════════════════════════ */
function TermsSection() {
  return (
    <div className="cs-section">
      <h1>Terms &amp; Conditions</h1>
      <p className="cs-updated">Last Updated: 03 October 2026</p>

      <p>
        Welcome to <strong>VKTrend</strong>. These Terms &amp; Conditions ("Terms")
        govern your use of our website <strong>vktrend.com</strong>, our mobile
        application, and all related services. By accessing or using any part of
        our platform, you confirm that you have read, understood, and agreed to
        be bound by these Terms. If you do not agree with any part of these Terms,
        please do not use our services.
      </p>

      <h2>1. About VKTrend</h2>
      <p>
        VKTrend is an online fashion retail platform based in Dumka, Jharkhand,
        India. We curate and sell apparel, footwear, and fashion accessories for
        men and women. We work closely with local vendors and trusted suppliers
        to bring you high-quality products at competitive prices. Our mission is
        to make premium fashion accessible to everyone in India, starting from
        the eastern region and expanding nationwide.
      </p>
      <p>
        <strong>Business Address:</strong> Dumka, Jharkhand – 814101, India<br />
        <strong>Email:</strong> support@vktrend.com<br />
        <strong>WhatsApp:</strong> +91 98765 43210
      </p>

      <h2>2. Account Registration</h2>
      <p>
        To place an order and access certain features, you may need to create an
        account. When registering, you agree to provide accurate, current, and
        complete information about yourself. You must be at least 18 years old
        to create an account and place orders on our platform. You are solely
        responsible for maintaining the confidentiality of your login credentials
        and for all activities that occur under your account. If you suspect any
        unauthorized use of your account, you must notify us immediately at
        support@vktrend.com. We reserve the right to suspend, restrict, or
        terminate accounts that violate these Terms or engage in fraudulent,
        abusive, or illegal activities.
      </p>

      <h2>3. Orders &amp; Payments</h2>
      <p>
        All orders placed on our platform are subject to product availability
        and confirmation of payment. Once you place an order, you will receive
        an order confirmation email and SMS with your unique order number. We
        accept payments through Cash on Delivery (COD), UPI, credit cards, debit
        cards, and net banking. All prices displayed on the website are in
        Indian Rupees (₹) and include applicable GST. We reserve the right to
        cancel any order at our discretion due to pricing errors, stock
        unavailability, or suspicious activity. In such cases, any amount paid
        will be refunded in full within 5–7 business days.
      </p>

      <h2>4. Shipping &amp; Delivery</h2>
      <p>
        We currently deliver across India through trusted courier partners. Our
        standard delivery time is 3–7 business days depending on your location.
        For orders above ₹999, shipping is absolutely free. For orders below
        ₹999, a flat delivery charge of ₹50 applies. Once your order is
        dispatched, you will receive a tracking number via email and SMS so you
        can monitor your delivery in real time. Please note that delivery times
        may be affected by weather conditions, festivals, or other factors
        beyond our control. VKTrend is not liable for delays caused by our
        courier partners or force majeure events.
      </p>

      <h2>5. Returns &amp; Refunds</h2>
      <p>
        We offer a 7-day return window from the date of delivery. To be eligible
        for a return, the product must be unused, unwashed, and in its original
        packaging with all tags and labels attached. Once we receive the returned
        product, our team performs a quality check. If the product meets our
        return criteria, we will process your refund within 3–5 business days.
        Refunds are issued to the original payment method or via UPI/bank
        transfer as per your preference. Please note that shipping charges are
        non-refundable unless the product was defective or incorrect. We do not
        accept returns for used, damaged, or counterfeit products.
      </p>

      <h2>6. Product Information</h2>
      <p>
        We make every effort to display accurate product colors, sizes, and
        descriptions on our website. However, actual colors may vary slightly
        due to differences in monitor and screen settings. Product images are
        for illustrative purposes only. Sizes may vary slightly across brands,
        and we recommend checking our size guide before placing an order. We
        reserve the right to modify, discontinue, or update product details at
        any time without prior notice.
      </p>

      <h2>7. Intellectual Property</h2>
      <p>
        All content available on the VKTrend website — including but not limited
        to logos, brand names, product images, graphics, text, code, and design
        elements — is the exclusive property of VKTrend and protected by Indian
        and international copyright laws. You may not copy, reproduce,
        distribute, modify, or exploit any part of our content without prior
        written permission. Any unauthorized use may result in legal action.
      </p>

      <h2>8. User Conduct</h2>
      <p>By using VKTrend, you agree NOT to:</p>
      <ul>
        <li>Use the website for any illegal or unauthorized purpose.</li>
        <li>Submit false, misleading, or fraudulent information.</li>
        <li>Attempt to hack, disrupt, or damage our services.</li>
        <li>Post abusive, obscene, defamatory, or hateful content.</li>
        <li>Violate any applicable local, state, or national laws.</li>
        <li>Impersonate any person or entity.</li>
      </ul>

      <h2>9. Limitation of Liability</h2>
      <p>
        VKTrend shall not be liable for any indirect, incidental, special, or
        consequential damages arising from the use of our platform, including
        but not limited to loss of profits, data, or goodwill. Our total
        liability to you for any claim shall not exceed the amount paid by you
        for the specific product or service giving rise to the claim.
      </p>

      <h2>10. Third-Party Services</h2>
      <p>
        We partner with trusted third-party providers for payments, logistics,
        and communication. These include Razorpay, Shiprocket, Delhivery, and
        WhatsApp Business. Each of these services operates under its own terms
        and privacy policies, which you should review separately. VKTrend is
        not responsible for the actions or omissions of these third parties.
      </p>

      <h2>11. Changes to Terms</h2>
      <p>
        We may update these Terms &amp; Conditions from time to time to reflect
        changes in our practices or legal requirements. Any changes will be
        posted on this page with an updated "Last Updated" date. Continued use
        of our platform after changes are posted constitutes your acceptance of
        the revised Terms.
      </p>

      <h2>12. Governing Law</h2>
      <p>
        These Terms are governed by and construed in accordance with the laws of
        India. Any disputes arising out of or in connection with these Terms
        shall be subject to the exclusive jurisdiction of the courts located in
        Dumka, Jharkhand.
      </p>

      <h2>13. Contact Us</h2>
      <p>If you have any questions about these Terms &amp; Conditions, please reach out to us:</p>
      <ul>
        <li><strong>Email:</strong> support@vktrend.com</li>
        <li><strong>WhatsApp:</strong> +91 98765 43210</li>
        <li><strong>Address:</strong> Dumka, Jharkhand – 814101, India</li>
      </ul>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   2. PRIVACY POLICY
═══════════════════════════════════════════════════════ */
function PrivacySection() {
  return (
    <div className="cs-section">
      <h1>Privacy Policy</h1>
      <p className="cs-updated">Last Updated: 03 October 2026</p>

      <p>
        At <strong>VKTrend</strong>, we take your privacy seriously. This
        Privacy Policy explains what information we collect, how we use it,
        how we protect it, and what rights you have regarding your personal
        data. By using our website or mobile app, you agree to the practices
        described in this policy.
      </p>

      <h2>1. Information We Collect</h2>
      <p>We collect the following types of information to provide you with the best shopping experience:</p>
      <ul>
        <li><strong>Personal Information:</strong> Your full name, email address, phone number, and shipping address.</li>
        <li><strong>Account Information:</strong> Username and encrypted password.</li>
        <li><strong>Order Information:</strong> Products purchased, order history, delivery status, and preferences.</li>
        <li><strong>Payment Information:</strong> Transaction IDs and payment method (we do NOT store card details).</li>
        <li><strong>Device &amp; Browsing Data:</strong> IP address, browser type, device model, and pages visited.</li>
        <li><strong>Communication Data:</strong> Emails, WhatsApp messages, and support tickets you send us.</li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <p>We use your information for the following purposes:</p>
      <ul>
        <li>To process, confirm, and deliver your orders.</li>
        <li>To send order updates, OTPs, and delivery notifications.</li>
        <li>To personalize your shopping experience and recommend products.</li>
        <li>To detect, prevent, and address fraud or security issues.</li>
        <li>To send promotional offers and newsletters (only if you opt-in).</li>
        <li>To improve our website, products, and services.</li>
        <li>To comply with legal obligations.</li>
      </ul>

      <h2>3. How We Share Your Information</h2>
      <p>We do not sell your personal data to third parties. However, we may share limited information with:</p>
      <ul>
        <li><strong>Courier Partners:</strong> Name, address, and phone number to deliver your order.</li>
        <li><strong>Payment Gateways:</strong> Necessary data to process transactions securely.</li>
        <li><strong>Email &amp; SMS Providers:</strong> To send transactional and promotional messages.</li>
        <li><strong>Analytics Providers:</strong> Anonymous usage data to improve our services.</li>
        <li><strong>Legal Authorities:</strong> When required by law or court order.</li>
      </ul>

      <h2>4. Data Security</h2>
      <p>
        We use industry-standard security measures to protect your data. All
        communications between your browser and our servers are encrypted using
        HTTPS. Passwords are stored using strong one-way hashing algorithms,
        meaning even our staff cannot read them. Payment information is handled
        entirely by PCI-DSS compliant payment gateways; we never see or store
        your card details. Our servers undergo regular security audits and
        updates to stay protected against emerging threats.
      </p>

      <h2>5. Cookies and Tracking</h2>
      <p>
        We use cookies and similar technologies to remember your cart, wishlist,
        and login session. Cookies help us understand how you use our site so we
        can improve it. You can disable cookies in your browser settings, but
        some features (like keeping items in your cart) may not work correctly.
      </p>

      <h2>6. Your Rights</h2>
      <p>You have the following rights regarding your personal data:</p>
      <ul>
        <li>Access your personal data anytime from your profile.</li>
        <li>Request correction of inaccurate or incomplete information.</li>
        <li>Request deletion of your account and associated data.</li>
        <li>Opt-out of marketing emails at any time.</li>
        <li>Object to certain types of data processing.</li>
        <li>Request a copy of your data in a portable format.</li>
      </ul>

      <h2>7. Data Retention</h2>
      <p>
        We retain your personal data only as long as necessary to fulfill the
        purposes described in this policy, or as required by law. Order history
        and transaction records are typically kept for 7 years for tax and legal
        compliance. When you delete your account, we remove your personal data
        within 30 days, except where we are legally required to retain it.
      </p>

      <h2>8. Children's Privacy</h2>
      <p>
        Our services are not intended for children under 18 years of age. We do
        not knowingly collect personal information from children. If we become
        aware that we have inadvertently collected data from a child, we will
        delete it promptly.
      </p>

      <h2>9. International Transfers</h2>
      <p>
        Your data is primarily stored and processed in India. If we transfer
        data to servers outside India (for example, for cloud storage), we
        ensure appropriate safeguards are in place to protect your information.
      </p>

      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes
        will be notified via email or a prominent notice on our website. Your
        continued use of VKTrend after changes are posted means you accept the
        revised policy.
      </p>

      <h2>11. Contact Us</h2>
      <p>For any privacy-related questions or concerns, please contact:</p>
      <ul>
        <li><strong>Email:</strong> privacy@vktrend.com</li>
        <li><strong>WhatsApp:</strong> +91 98765 43210</li>
        <li><strong>Address:</strong> Dumka, Jharkhand – 814101, India</li>
      </ul>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   3. SHIPPING INFO
═══════════════════════════════════════════════════════ */
function ShippingSection() {
  return (
    <div className="cs-section">
      <h1>Shipping Information</h1>
      <p className="cs-updated">Everything you need to know about delivery</p>

      <p>
        At <strong>VKTrend</strong>, we understand that fast and reliable
        shipping is essential to a great shopping experience. We have partnered
        with India's most trusted courier services to ensure your order reaches
        you safely and on time. Below is everything you need to know about our
        shipping process, timelines, charges, and tracking.
      </p>

      <h2>1. Delivery Timelines</h2>
      <p>
        Our standard delivery time is <strong>3–7 business days</strong> across
        India. Delivery time depends on your location and the courier partner.
        For customers in Dumka, Dhanbad, and nearby areas of Jharkhand, we often
        deliver within <strong>2–3 business days</strong>. Metro cities like
        Delhi, Mumbai, Bangalore, and Kolkata typically receive orders in 3–5
        days. Remote areas and the North-East may take up to 7–10 business days.
      </p>

      <h2>2. Shipping Charges</h2>
      <p>We keep our shipping charges simple and transparent:</p>
      <ul>
        <li><strong>Free Shipping</strong> on all orders above ₹999.</li>
        <li><strong>₹50 flat charge</strong> on orders below ₹999.</li>
        <li><strong>Cash on Delivery (COD):</strong> Additional ₹20 handling fee.</li>
        <li><strong>Same-Day Delivery:</strong> Available in select Dumka pincodes for ₹79 (order before 11 AM).</li>
      </ul>

      <h2>3. Order Processing Time</h2>
      <p>
        Orders placed before <strong>2:00 PM IST</strong> on any working day are
        typically processed and dispatched on the same day. Orders placed after
        2:00 PM, on Sundays, or on public holidays are processed on the next
        working day. Once your order is dispatched, you will receive a
        confirmation email and SMS with your tracking number.
      </p>

      <h2>4. Order Tracking</h2>
      <p>
        Once your order is shipped, you will receive a tracking number and a
        direct link to the courier's website. You can use this tracking number
        to monitor your delivery in real time. You can also track your order
        directly from the "My Orders" section of your VKTrend account. The
        tracking page will show you the current status: dispatched, in transit,
        out for delivery, or delivered.
      </p>

      <h2>5. Courier Partners</h2>
      <p>
        We work with multiple trusted courier partners including Delhivery,
        Blue Dart, DTDC, Ekart, Shadowfax, Xpressbees, and Ecom Express. The
        courier partner for your order will depend on your pincode and the
        fastest available service. All our partners provide end-to-end tracking
        and OTP-based delivery for maximum security.
      </p>

      <h2>6. Delivery Attempts</h2>
      <p>
        Our courier partners attempt delivery up to <strong>3 times</strong> on
        consecutive days. If you are unavailable on all three attempts, the
        parcel is returned to us. In such cases, we will contact you to arrange
        a re-delivery (extra shipping charges may apply) or process a refund
        (excluding shipping charges).
      </p>

      <h2>7. Address Accuracy</h2>
      <p>
        Please ensure your shipping address, phone number, and pincode are
        accurate before placing an order. We are not responsible for deliveries
        to incorrect addresses provided by the customer. If you notice a mistake,
        contact us immediately at support@vktrend.com — we can only update the
        address before the order is dispatched.
      </p>

      <h2>8. Delivery OTP</h2>
      <p>
        For your safety, all deliveries require OTP verification. When the
        delivery agent arrives, they will ask for a 4–6 digit OTP that will be
        sent to your registered mobile number. Please keep your phone nearby
        during the expected delivery window.
      </p>

      <h2>9. Shipping Restrictions</h2>
      <ul>
        <li>We currently ship only within India.</li>
        <li>Some remote pincodes may not be serviceable.</li>
        <li>We do not ship to P.O. boxes.</li>
        <li>Delivery to defence and restricted areas may take longer.</li>
      </ul>

      <h2>10. Damaged or Lost Shipments</h2>
      <p>
        If your package arrives damaged or is lost in transit, please contact us
        within <strong>48 hours</strong> of delivery (or expected delivery) at
        support@vktrend.com. Provide your order number and photos of the damaged
        package. We will arrange a replacement or full refund within 5–7
        business days.
      </p>

      <h2>11. Contact for Shipping Queries</h2>
      <ul>
        <li><strong>Email:</strong> shipping@vktrend.com</li>
        <li><strong>WhatsApp:</strong> +91 98765 43210</li>
        <li><strong>Hours:</strong> Mon–Sat, 10 AM – 7 PM IST</li>
      </ul>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   4. RETURNS & EXCHANGES
═══════════════════════════════════════════════════════ */
function ReturnsSection() {
  return (
    <div className="cs-section">
      <h1>Returns &amp; Exchanges</h1>
      <p className="cs-updated">Easy returns, simple refunds</p>

      <p>
        At <strong>VKTrend</strong>, your satisfaction is our top priority. If
        you are not completely happy with your purchase, we offer a hassle-free
        return and exchange policy. Below is everything you need to know about
        how to return or exchange a product, our refund timelines, and the
        conditions that apply.
      </p>

      <h2>1. Return Window</h2>
      <p>
        We offer a <strong>7-day return window</strong> from the date of
        delivery. If 7 days have passed since delivery, unfortunately we cannot
        offer a return or exchange. The return window starts from the day your
        order is marked as "Delivered" on our system. For orders marked as
        delivered but not received, please contact us within 48 hours.
      </p>

      <h2>2. Return Eligibility</h2>
      <p>To be eligible for a return, the product must meet the following conditions:</p>
      <ul>
        <li>Product must be unused and unwashed.</li>
        <li>Original tags, labels, and packaging must be intact.</li>
        <li>Product must be in the same condition as received.</li>
        <li>Original invoice/receipt must be included.</li>
        <li>Product must not be damaged due to customer misuse.</li>
        <li>Product must not be a "final sale" or "non-returnable" item.</li>
      </ul>

      <h2>3. How to Initiate a Return</h2>
      <p>Initiating a return is easy:</p>
      <ul>
        <li>Log in to your VKTrend account.</li>
        <li>Go to "My Orders" and open the order you want to return.</li>
        <li>Click on "Request Return / Exchange".</li>
        <li>Select the reason for return and provide details.</li>
        <li>Choose your refund method (original payment, UPI, or bank).</li>
        <li>Submit the request. Our team will review it within 24 hours.</li>
      </ul>

      <h2>4. Return Pickup</h2>
      <p>
        Once your return is approved, our courier partner will pick up the
        product from your address within <strong>24–48 hours</strong>. Return
        pickup is completely <strong>free</strong> for defective, damaged, or
        incorrect products. For other returns, a pickup fee of ₹50 may apply
        (deducted from the refund amount).
      </p>

      <h2>5. Refund Timeline</h2>
      <p>
        After we receive the returned product, our quality team inspects it
        within 24 hours. If approved, the refund is processed within 3–5
        business days. Refunds are made to:
      </p>
      <ul>
        <li><strong>Original Payment Method:</strong> For online payments (card, UPI, net banking).</li>
        <li><strong>Bank Transfer:</strong> For COD orders, we transfer to your bank account.</li>
        <li><strong>VKTrend Wallet:</strong> Instant refund with 5% bonus (optional).</li>
      </ul>

      <h2>6. Exchange Policy</h2>
      <p>
        We offer free exchanges for size issues and defective products. If you
        received the wrong size or a damaged item, we will send you a replacement
        free of cost. Exchanges are subject to stock availability. If the
        desired size or color is out of stock, we will process a full refund
        instead.
      </p>

      <h2>7. Non-Returnable Items</h2>
      <p>The following items cannot be returned:</p>
      <ul>
        <li>Innerwear, socks, and personal hygiene products.</li>
        <li>Products marked "Final Sale" or "Non-Returnable".</li>
        <li>Products damaged due to customer misuse.</li>
        <li>Products without original tags or packaging.</li>
        <li>Gift cards and digital products.</li>
      </ul>

      <h2>8. Damaged or Wrong Product</h2>
      <p>
        If you received a damaged, defective, or wrong product, please contact
        us within <strong>48 hours</strong> of delivery. Send us a photo or
        video of the product and your order number to support@vktrend.com. We
        will arrange a free replacement or a full refund, including shipping
        charges.
      </p>

      <h2>9. Cancellation Policy</h2>
      <p>
        You can cancel your order free of charge any time before it is shipped.
        Once shipped, orders cannot be cancelled but can be returned after
        delivery. To cancel, go to "My Orders" and click "Cancel Order". Refunds
        for cancellations are processed within 3–5 business days.
      </p>

      <h2>10. Refund for COD Orders</h2>
      <p>
        For Cash on Delivery orders, refunds cannot be made in cash. We will
        transfer the amount to your UPI ID or bank account within 5–7 business
        days of return approval. Please provide accurate UPI/bank details during
        the return request.
      </p>

      <h2>11. Contact for Returns</h2>
      <ul>
        <li><strong>Email:</strong> returns@vktrend.com</li>
        <li><strong>WhatsApp:</strong> +91 98765 43210</li>
        <li><strong>Hours:</strong> Mon–Sat, 10 AM – 7 PM IST</li>
      </ul>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   5. FAQ
═══════════════════════════════════════════════════ */
function FAQSection() {
  const faqs = [
    {
      q: 'How do I place an order on VKTrend?',
      a: 'Simply browse our collection, select the size and quantity, and click "Add to Cart" or "Buy Now". Then proceed to checkout, enter your shipping details, choose a payment method, and place your order. You will receive an order confirmation email and SMS immediately.'
    },
    {
      q: 'What payment methods do you accept?',
      a: 'We accept Cash on Delivery (COD), UPI (Google Pay, PhonePe, Paytm), credit cards, debit cards, and net banking. All online payments are processed through secure PCI-DSS compliant gateways.'
    },
    {
      q: 'How long does delivery take?',
      a: 'Standard delivery takes 3–7 business days across India. For Dumka, Dhanbad, and nearby areas, we often deliver within 2–3 business days. Metro cities usually receive orders in 3–5 days.'
    },
    {
      q: 'Is shipping free?',
      a: 'Yes! We offer free shipping on all orders above ₹999. For orders below ₹999, a flat ₹50 delivery charge applies. COD orders have an additional ₹20 handling fee.'
    },
    {
      q: 'How can I track my order?',
      a: 'Once your order is shipped, you will receive a tracking number via email and SMS. You can use this number to track your order on the courier\'s website. You can also track it from the "My Orders" section of your VKTrend account.'
    },
    {
      q: 'Can I return or exchange a product?',
      a: 'Yes, we offer a 7-day return and exchange window from the date of delivery. The product must be unused, unwashed, and in its original packaging with all tags intact. Simply go to "My Orders" and click "Request Return / Exchange".'
    },
    {
      q: 'How long does a refund take?',
      a: 'After we receive and inspect the returned product, refunds are processed within 3–5 business days. For online payments, the amount is credited to your original payment method. For COD orders, we transfer to your UPI or bank account.'
    },
    {
      q: 'Do you offer Cash on Delivery?',
      a: 'Yes, we offer Cash on Delivery across India. An additional ₹20 handling fee applies for COD orders. For safety, delivery agents will ask for an OTP sent to your registered mobile number.'
    },
    {
      q: 'What if I receive a damaged or wrong product?',
      a: 'If you receive a damaged, defective, or incorrect product, contact us within 48 hours at support@vktrend.com with photos and your order number. We will arrange a free replacement or full refund, including shipping charges.'
    },
    {
      q: 'How do I cancel my order?',
      a: 'You can cancel your order free of charge anytime before it is shipped. Go to "My Orders" and click "Cancel Order". Once shipped, orders cannot be cancelled but can be returned after delivery.'
    },
    {
      q: 'Do you ship outside India?',
      a: 'Currently, we ship only within India. We are working on international shipping and will announce it soon. Stay tuned!'
    },
    {
      q: 'How can I contact customer support?',
      a: 'You can reach us via email at support@vktrend.com, WhatsApp at +91 98765 43210, or through the Contact Us form on our website. Our support hours are Monday to Saturday, 10 AM to 7 PM IST.'
    },
    {
      q: 'Is my personal information safe?',
      a: 'Absolutely. We use industry-standard HTTPS encryption, secure password hashing, and PCI-DSS compliant payment gateways. We never sell your data to third parties. Read our Privacy Policy for full details.'
    },
    {
      q: 'Do you have a physical store?',
      a: 'Currently, VKTrend is an online-only store. However, we are planning to open a physical store in Dumka, Jharkhand soon. Follow us on Instagram @vktrend.online for updates.'
    },
    {
      q: 'How do I know my correct size?',
      a: 'Each product page has a detailed size chart with measurements in inches and centimeters. We recommend measuring yourself and comparing with our chart before ordering. If you are between sizes, we suggest ordering the larger size.'
    },
  ];

  return (
    <div className="cs-section">
      <h1>Frequently Asked Questions</h1>
      <p className="cs-updated">Quick answers to common questions</p>

      <p>
        Welcome to the VKTrend FAQ section. Here you will find answers to the
        most common questions our customers ask. If your question is not
        answered here, please reach out to our support team — we are always
        happy to help!
      </p>

      <div className="cs-faq-list">
        {faqs.map((faq, i) => (
          <details key={i} className="cs-faq-item">
            <summary className="cs-faq-question">
              <span className="cs-faq-num">{i + 1}.</span>
              {faq.q}
            </summary>
            <p className="cs-faq-answer">{faq.a}</p>
          </details>
        ))}
      </div>

      <div className="cs-faq-contact">
        <h2>Still Have Questions?</h2>
        <p>
          Our customer support team is here to help. Reach out to us via email,
          WhatsApp, or phone and we will respond within 24 hours.
        </p>
        <ul>
          <li><strong>Email:</strong> support@vktrend.com</li>
          <li><strong>WhatsApp:</strong> +91 98765 43210</li>
          <li><strong>Hours:</strong> Mon–Sat, 10 AM – 7 PM IST</li>
        </ul>
      </div>
    </div>
  );
}